import type { DistributedOmit } from 'type-fest';
import type { PromiseActorLogic } from 'xstate';

import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { inferDecisionMakers, inferPetitionScopeLocation } from '#src/app/pages/startAPetitionV2/shared/api';
import type { PetitionDraft } from '#src/app/shared/api/petitionDraft';

import type { StartAPetitionPageData } from '../pageData';

import type { machine as controlActor } from './actors/control/actor';

export type Draft = Partial<PetitionDraft>;

export const MediaTypes = { AI: 'AI_GENERATED', USER: 'USER_UPLOADED' } as const;

export type StateInput = object;

export type StateContext = { draft?: Draft | null };

/**
 * What the orchestration machine's operations need from the page, supplied fresh on every page load
 * and never persisted: the network client, tracking, and FCMs. FCMs are evaluated at runtime, so a
 * returning draft has its operations switched on or off by the current values. Anything that must
 * stay stable for a draft (the experiment assignment in `metadata.variant`, operation results)
 * belongs on the draft instead.
 */
export type OrchestrationDeps = {
	utilityContext: UtilityContext;
	track: TrackingFn;
	featureConfigs: StartAPetitionPageData['featureConfigs'];
};

export type InferScopeLocationInput = Parameters<typeof inferPetitionScopeLocation>[0];
export type InferScopeLocationOutput = Awaited<ReturnType<typeof inferPetitionScopeLocation>>;
/** How local the petition's target is; decision makers are only inferred for a local target. */
type DecisionMakersTargetScope = 'LOCAL' | 'NATIONAL' | 'GLOBAL';
/** The `inferDecisionMakers` api input plus the target's scope, so eligibility is decided before the call. */
export type InferDecisionMakersInput = Parameters<typeof inferDecisionMakers>[0] & {
	scope: DecisionMakersTargetScope | null;
};
export type InferDecisionMakersOutput = Awaited<ReturnType<typeof inferDecisionMakers>>;

/** Sent by a variant to start a background operation; `name` picks the operation's region. */
export type JobStartEvent =
	| { type: 'job.start'; name: 'inferScopeLocation'; input: InferScopeLocationInput }
	| { type: 'job.start'; name: 'inferDecisionMakers'; input: InferDecisionMakersInput };

/** Raised when an operation succeeds and relayed to the active variant, which applies the result. */
export type JobDoneEvent =
	| { type: 'job.done'; name: 'inferScopeLocation'; output: InferScopeLocationOutput }
	| { type: 'job.done'; name: 'inferDecisionMakers'; output: InferDecisionMakersOutput };

export type StateEvents =
	| { type: 'back'; target?: null }
	| { type: 'submit'; draft?: Draft; target?: string | null }
	| { type: 'error' }
	// Merges `draft` into the variant actor's draft without navigating; handled at the variant's root.
	| { type: 'patch'; draft: Draft }
	| JobStartEvent
	| JobDoneEvent;

export type StateActions = {
	onSubmit: () => void;
	onPatch: () => void;
	applyScopeLocation: () => void;
	requestDecisionMakers: () => void;
	applyDecisionMakers: () => void;
	raiseJobDone: (_: unknown, params: DistributedOmit<JobDoneEvent, 'type'>) => void;
	onError: () => void;
	// TODO: these actions should be removed after separating the control machine
	persistDraftType: () => void;
	unsetDraftType: () => void;
};

export type StateGuards = {
	isControl: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	canStartInferScopeLocation: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	canStartInferDecisionMakers: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isJobDone: (_: unknown, params: { name: JobDoneEvent['name'] }) => boolean;
	// TODO: these guards should be removed after separating the control machine
	transitionTo: (_: unknown, params: { target: string }) => boolean;
	isUserDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
	isAiDraft: (args: { context: StateContext; event: StateEvents }, params?: unknown) => boolean;
};

export type StateChildren = {
	controlActorId: 'controlActorSrc';
	inferScopeLocationActorId: 'inferScopeLocationSrc';
	inferDecisionMakersActorId: 'inferDecisionMakersSrc';
};

export type StateActors = {
	controlActorSrc: typeof controlActor;
	inferScopeLocationSrc: PromiseActorLogic<InferScopeLocationOutput, InferScopeLocationInput>;
	inferDecisionMakersSrc: PromiseActorLogic<InferDecisionMakersOutput, InferDecisionMakersInput>;
};
